import React, { useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Tag } from "@design-system/Tag/Tag";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { EllipsisWithTooltip } from "@design-system/EllipsisWithTooltip/EllipsisWithTooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CounterpartyDirectoryViewRow } from "../counterpartyDirectoryViewTypes";
import styles from "../DirectoryTable.module.scss";

export const CounterpartyAccountsCell: React.FC<
    CellContext<
        ViewTableFeatures,
        CounterpartyDirectoryViewRow,
        CounterpartyDirectoryViewRow[] | undefined
    >
> = React.memo(({ row }) => {
    const labels = useMemo(() => {
        if (row.original.isAccountRow) {
            return [];
        }
        const deduped = new Set<string>();
        return (row.original.children ?? []).reduce<string[]>(
            (acc, account) => {
                const label = account.name;
                if (!deduped.has(label)) {
                    deduped.add(label);
                    acc.push(label);
                }
                return acc;
            },
            [],
        );
    }, [row.original.children, row.original.isAccountRow]);

    const [first, ...other] = labels;

    if (!first) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell>
            <div className={styles.accounts}>
                <EllipsisWithTooltip
                    fullText={first}
                    overflowDirection="vertical"
                >
                    {(ref) => (
                        <span className={styles.accountLabel} ref={ref}>
                            {first}
                        </span>
                    )}
                </EllipsisWithTooltip>

                {other.length > 0 && (
                    <Tooltip bullets={other}>
                        <span>
                            <Tag size="sm" color="neutral" outline numeric>
                                +{other.length}
                            </Tag>
                        </span>
                    </Tooltip>
                )}
            </div>
        </ViewTableCell>
    );
});

CounterpartyAccountsCell.displayName = "CounterpartyAccountsCell";
